import { Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core'
import { I18nRequest, I18nResponse } from '@interfaces/i-i18n'
import { TranslationCacheService } from '@services/translation-cache/translation-cache.service'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

/**
 * @description Provides the option of fetching an I18n value from within a template. This removes the need to import
 * the translation service within each component causing unnessary dependencies and elongating the writing of unit tests.
 */

@Directive({
  selector: '[appI18n]',
  standalone: true,
})
export class InlineTranslationDirective implements OnInit, OnDestroy {
  @Input('appI18n') i18n!: I18nRequest

  private unsubscribe$: Subject<null> = new Subject()

  constructor(private elementRef: ElementRef, private translationCache: TranslationCacheService) {}

  ngOnInit(): void {
    this.assignDefault()
    this.translationCache.translationCache$.pipe(takeUntil(this.unsubscribe$)).subscribe((res: I18nResponse) => this.assignValue(res))
    this.translationCache.requestTranslation(this.i18n)
  }

  /**
   * As directive can't inherit classes, the unsubscribe logic must be included manually here.
   */
  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  /**
   * Avoids the potential for 'undefined' to be displayed whilst the translationCache resolves.
   */
  private assignDefault(): void {
    this.elementRef.nativeElement.innerText = this.i18n.default
  }

  private assignValue(response: I18nResponse): void {
    this.elementRef.nativeElement.innerText = response[this.i18n.selector ?? this.i18n.code]?.value
  }
}
